/**
 * @file Vue Router 路由入口
 * @description 定义前端路由表、登录态守卫、路由刷新标记和 chunk 加载失败恢复逻辑。
 */
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw
} from 'vue-router'
import { grayReleaseStore, loginUserStore, useRouteRefreshStore } from '@/stores'
import { ElMessage } from 'element-plus'
import {
  clearMangaBreadcrumbTitle,
  resolveMangaSourcePath,
  shouldClearMangaBreadcrumbTitle,
  writeMangaSourcePath
} from '@/utils/mangaBreadcrumb'
import {
  shouldResetLayoutScroll,
  shouldTouchRouteRefresh
} from '@/utils/routeViewState'
import { showEditorDisabledMessage } from '@/utils/editorDisabled'
import { canMemberAccessNavItem, type NavItemKey } from '@/utils/navPermission'

/** 应用路由表 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    component: () => import('@/views/login/LoginPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    component: () => import('@/views/layout/LayoutContainer.vue'),
    redirect: '',
    children: [
      {
        path: '/dengdeng-bot',
        name: 'dengdeng-bot',
        component: () => import('@/views/agent/DengdengBotPage.vue'),
        meta: {
          requiresAuth: true,
          permission: 'dengdengAgent',
          adminOnly: true,
          grayFeature: 'dengdeng-agent'
        }
      },
      {
        path: '',
        component: () => import('@/views/pages/HomePage.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: '/workbench',
        name: 'my-workbench',
        component: () => import('@/views/workbench/MyResourcesWorkbench.vue'),
        meta: { requiresAuth: true, noCache: true }
      },
      {
        path: '/aboutus',
        component: () => import('@/views/pages/AboutUs.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: '/anniversary-10th',
        component: () => import('@/views/pages/events/AnniversaryEvent.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/uploaded',
        component: () => import('@/views/pages/UploadedPage.vue'),
        meta: { noCache: true }
      },
      {
        path: '/search',
        name: 'global-search',
        component: () => import('@/views/search/GlobalSearchPage.vue'),
        meta: { requiresAuth: true, noCache: true }
      },
      {
        path: '/manga/manage',
        component: () => import('@/views/pages/admin/AdminPage.vue'),
        meta: { requiresAuth: true, permission: 'publish' }
      },
      {
        path: '/task-tracking',
        component: () => import('@/views/pages/admin/TaskTracking.vue'),
        meta: { requiresAuth: true, permission: 'monitor', noCache: true }
      },
      {
        path: '/admin/manga-update-monitor',
        name: 'manga-update-monitor',
        component: () =>
          import('@/views/pages/admin/MangaUpdateMonitor.vue'),
        meta: { requiresAuth: true, permission: 'monitor' }
      },
      {
        path: '/admin/operation-logs',
        name: 'operation-logs',
        component: () => import('@/views/pages/admin/OperationLogPage.vue'),
        meta: {
          requiresAuth: true,
          permission: 'operationLog',
          noCache: true
        }
      },
      {
        path: '/admin/gray',
        component: () => import('@/views/pages/admin/GrayReleaseManage.vue'),
        meta: { requiresAuth: true, noCache: true, adminOnly: true }
      },
      {
        path: '/admin/posts',
        component: () => import('@/views/pages/admin/PostCatalogManage.vue'),
        meta: {
          requiresAuth: true,
          permission: 'postCatalog',
          noCache: true
        }
      },
      {
        path: '/manga/author',
        component: () => import('@/views/manage/AuthorManage.vue')
      },
      {
        path: '/manga/magazine',
        component: () => import('@/views/manage/MagazineManage.vue')
      },
      {
        path: '/manga/:mangaId',
        name: 'manga',
        component: () => import('@/views/manga/MangaEpisode.vue'),
        props: (route) => ({
          mangaId: route.params.mangaId
        }),
        meta: {
          requiresAuth: false,
          noCache: true,
          preserveOnQuery: true
        }
      },
      {
        path: '/author/:authorId',
        name: 'author',
        component: () => import('@/views/manga/AuthorManga.vue'),
        props: (route) => ({
          authorId: route.params.authorId
        }),
        meta: { requiresAuth: false }
      },
      {
        path: '/magazine/:magazineId',
        name: 'magazine',
        component: () => import('@/views/manga/MagazineManga.vue'),
        props: (route) => ({
          magazineId: route.params.magazineId
        }),
        meta: { requiresAuth: false }
      },
      {
        path: '/member/list',
        component: () => import('@/views/pages/MemberList.vue')
      },
      {
        path: '/member/profile',
        component: () => import('@/views/user/UserProfile.vue')
      },
      {
        path: '/member/avatar',
        component: () => import('@/views/user/UserProfile.vue')
      },
      {
        path: '/member/:memberId',
        name: 'member',
        component: () => import('@/views/pages/MemberPage.vue'),
        props: (route) => ({
          memberId: route.params.memberId
        }),
        meta: { noCache: true }
      },
      {
        path: '/social-links',
        name: 'social-links',
        component: () => import('@/views/pages/SocialLinks.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: '/evaluation',
        name: 'evaluation',
        component: () => import('@/views/evaluation/EvaluationPage.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/questionnaire',
        name: 'questionnaire',
        component: () =>
          import('@/views/questionnaire/QuestionnaireOverview.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/tickets',
        name: 'tickets',
        component: () => import('@/views/tickets/TicketListPage.vue'),
        meta: { requiresAuth: true, permission: 'tickets', noCache: true }
      },
      {
        path: '/tickets/:id',
        name: 'ticket-detail',
        component: () => import('@/views/tickets/TicketDetailPage.vue'),
        props: (route) => ({ id: Number(route.params.id) }),
        meta: { requiresAuth: true, permission: 'tickets', noCache: true }
      },
      {
        path: '/member/souvenir',
        name: 'souvenir',
        component: () => import('@/views/pages/Membersouvenir.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/403',
        name: 'forbidden',
        component: () => import('@/views/pages/Error403.vue'),
        meta: { requiresAuth: false, noCache: true }
      },
      {
        path: '/editor/episode/:episodeId',
        name: 'episode-editor',
        component: () => import('@/views/editor/EpisodeEditorView.vue'),
        props: (route) => ({ episodeId: route.params.episodeId }),
        meta: { requiresAuth: true, noCache: true, editorChrome: true }
      },
      {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/pages/Error404.vue'),
        meta: { requiresAuth: false, noCache: true }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

/** 部署后旧 chunk 404 时自动刷新一次，拉取新 index */
const isChunkLoadError = (message: string): boolean =>
  /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk .* failed/i.test(
    message
  )

router.onError((error, to) => {
  const msg = error instanceof Error ? error.message : String(error)
  if (!isChunkLoadError(msg)) {
    throw error
  }
  const reloadKey = `chunk-reload:${to.fullPath}`
  if (!sessionStorage.getItem(reloadKey)) {
    sessionStorage.setItem(reloadKey, '1')
    window.location.assign(to.fullPath)
    return
  }
  throw error
})

/**
 * 解析在线编辑器被禁用时的回退页面。
 * 优先返回来源页；只有首次直达或来源无效时才兜底到工作台。
 */
const resolveEditorDisabledFallback = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): string => {
  if (
    from.matched.length > 0 &&
    from.name !== 'episode-editor' &&
    from.fullPath &&
    from.fullPath !== to.fullPath &&
    from.fullPath.startsWith('/')
  ) {
    return from.fullPath
  }
  return '/workbench'
}

router.beforeEach(async (to, from, next) => {
  const useStore = loginUserStore()
  const requiresAuth = to.meta.requiresAuth !== false

  if (shouldTouchRouteRefresh(to, from)) {
    const refreshStore = useRouteRefreshStore()
    refreshStore.touch(to.path)
  }

  if (requiresAuth && !useStore.token) {
    ElMessage.warning('请先登录喵！')
    next('/login')
    return
  }

  const permission = to.meta.permission as NavItemKey | undefined
  if (permission) {
    try {
      const member = await useStore.refreshMemberRef()
      if (!canMemberAccessNavItem(permission, member.value)) {
        next({ name: 'forbidden', replace: true })
        return
      }
    } catch {
      // 权限资料无法确认时拒绝访问，避免网络异常绕过受保护路由。
      next({ name: 'forbidden', replace: true })
      return
    }
  }

  if (to.meta.adminOnly) {
    try {
      const member = await useStore.refreshMemberRef()
      const isAdmin = member.value?.posts?.some(
        (item: { post?: number }) => item.post?.toString() === '4'
      )
      if (!isAdmin) {
        next({ name: 'forbidden', replace: true })
        return
      }
    } catch {
      next({ name: 'forbidden', replace: true })
      return
    }
  }

  const grayFeature = to.meta.grayFeature as string | undefined
  if (grayFeature) {
    const grayStore = grayReleaseStore()
    if (!grayStore.loaded) await grayStore.refreshCurrent()
    if (!grayStore.isEnabled(grayFeature)) {
      next({ name: 'forbidden', replace: true })
      return
    }
  }

  if (to.name === 'episode-editor') {
    showEditorDisabledMessage()
    next(resolveEditorDisabledFallback(to, from))
    return
  }

  next()
})

/**
 * 路由切换后同步漫画来源并将自定义滚动容器滚回顶部。
 * @param to 已进入的目标路由。
 * @param from 离开的来源路由。
 */
router.afterEach((to, from) => {
  const mangaSourcePath = resolveMangaSourcePath(to.path, {
    path: from.path,
    fullPath: from.fullPath,
    matchedCount: from.matched.length
  })
  if (mangaSourcePath) {
    writeMangaSourcePath(mangaSourcePath)
  }
  if (
    shouldClearMangaBreadcrumbTitle(
      to.name,
      to.params.mangaId,
      from.name,
      from.params.mangaId
    )
  ) {
    clearMangaBreadcrumbTitle()
  }
  if (shouldResetLayoutScroll(to, from)) {
    document.getElementById('layout-scroll')?.scrollTo(0, 0)
  }
})
export default router

export { loginUserStore as useMemberStore }
